// Menu ordering for the public venue page.
//
// Menus are fetched without a Firestore orderBy: orderBy("createdOn") silently
// drops every doc that lacks the field, and Square-imported menus
// (menuSource: "square") are written without it, so venues like Lumen showed
// "No menu available yet". Sort here instead: menus with createdOn first
// (oldest first), then the rest; ties and missing dates fall back to name.

const toMillis = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

const menuName = (m) => String(m?.menuName || m?.menuType || "");

export function compareMenus(a, b) {
  const ta = toMillis(a?.createdOn);
  const tb = toMillis(b?.createdOn);
  if (ta !== null && tb !== null && ta !== tb) return ta - tb;
  if (ta !== null && tb === null) return -1;
  if (ta === null && tb !== null) return 1;
  return menuName(a).localeCompare(menuName(b), "en", { sensitivity: "base", numeric: true });
}

/** Returns a new sorted array; the input is not modified. */
export const sortMenus = (menus) => [...(menus || [])].filter(Boolean).sort(compareMenus);

// ── Menu availability (G10), matching the app's util.isMenuOpen ─────────────
// Off menus are still shown, marked "(currently off)", after the open ones.
// Differences from the app: missing `menuOn` counts as on (legacy/scraped
// menus; the app treats it as off), times use the venue's time zone, and
// windows that pass midnight (e.g. 18:00–02:00) work.

const parseHM = (v) => {
  const m = String(v ?? "").trim().match(/^(\d{1,2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const minutesNow = (now, timeZone) => {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
      }).formatToParts(now);
      const get = (t) => Number(parts.find((p) => p.type === t)?.value);
      return (get("hour") % 24) * 60 + get("minute");
    } catch {
      // unknown zone: use the browser clock
    }
  }
  return now.getHours() * 60 + now.getMinutes();
};

export function isMenuOpenNow(menu, venue, now = new Date()) {
  if (menu?.menuOn === false) return false;
  const on = parseHM(menu?.menuOnTime);
  const off = parseHM(menu?.menuOffTime);
  if (on === null || off === null || on === off) return true;
  const t = minutesNow(now, venue?.bizTimeZone);
  return on < off ? t >= on && t < off : t >= on || t < off;
}

/** Open menus first, then off menus; each group keeps sortMenus order. */
export function orderMenusForDisplay(menus, venue, now = new Date()) {
  const sorted = sortMenus(menus);
  const open = [];
  const off = [];
  sorted.forEach((m) => (isMenuOpenNow(m, venue, now) ? open : off).push(m));
  return [...open, ...off].map((m) => ({ ...m, _isOff: off.includes(m) }));
}
